import type { EndorsementPremiumChange } from '@domain/schemas/endorsement-premium-change';
import type { AuditAction, AuditSurface, AuditTarget } from '@domain/types/audit-log';
import { getConditionalPremiumLabel } from '@helpers/getConditionalPremiumLabel';

// Copy seam — all catalogue wording lives here so Phase 2 (full backend emission) requires no FE changes.
// The `naming-mrc.ts` language layer (MRC-specific terms) is a Phase 2 concern.

// The noun qualifies the datapoint category for surfaces where it adds meaning.
// `null` → "Edited/Cleared the datapoint {name}"; '' → endorsement-summary special case (no prefix).
export const FORM_UPDATED_NOUN: Record<AuditSurface, string | null> = {
  contract: null,
  template: 'default content',
  endorsement: null,
  'endorsement-summary': '', // special: renders as "Edited {field}" with no datapoint prefix
  library: null,
};

export const RESOURCE_SYNCED_LABEL = 'Synced contract data with PPL';

/** Resource noun for lifecycle/detail messages ("Renamed this {noun} to …"). */
export const RESOURCE_NOUN: Record<AuditSurface, string> = {
  contract: 'contract',
  template: 'template',
  endorsement: 'endorsement',
  'endorsement-summary': 'endorsement',
  library: 'item',
};

/**
 * Actions on the 'endorsement' surface shown in BOTH the Endorsement View panel and Contract/Form View panels.
 * These affect the endorsement as a whole (not just the summary document or just the contract document).
 */
export const ENDORSEMENT_ACTIONS_SHARED_WITH_SUMMARY = [
  'name.changed',
  'status.changed',
  'part.removed',
  'part.moved',
  'part.swapped',
  'endorsement.raised',
  'endorsement.details_edited',
  'endorsement.duplicated',
  'endorsement.duplicated_from',
] as const satisfies ReadonlyArray<AuditAction['action']>;

/** Kind → noun, with no surface qualifier. */
export const KIND_NOUN = (kind: AuditTarget['kind'] | null | undefined): string => {
  switch (kind) {
    case 'clause':
      return 'clause';
    case 'attachment':
      return 'attachment';
    case 'heading':
      return 'heading';
    case 'section':
      return 'section';
    default:
      return 'block';
  }
};

export const BLOCK_KIND_NOUN = (surface: AuditSurface, kind: AuditTarget['kind'] | null | undefined): string => {
  const noun = KIND_NOUN(kind);
  return surface === 'template' && (noun === 'block' || noun === 'clause') ? `library ${noun}` : noun;
};

const ENDORSEMENT_FIELD_NOUN: Record<string, string> = {
  effective_date: 'Effective Date',
  expiry_date: 'Expiry Date',
  type: 'Endorsement Type',
  reason: 'Reason',
  premium_change: 'Premium Change',
  additional_premium: 'Additional premium',
  internal_description: 'Internal Description',
};

/** Endorsement metadata field key → the label the Edit Endorsement modal shows for it. */
export const endorsementFieldNoun = (field: string, premiumChange?: string): string => {
  if (field === 'additional_premium') {
    const conditional = getConditionalPremiumLabel(premiumChange as EndorsementPremiumChange | undefined);
    if (conditional) return conditional;
  }
  return ENDORSEMENT_FIELD_NOUN[field] ?? field;
};
